import { CollectBillingDataInput } from "@/modules/patients/detail/conclude/billing-section/services/__generated__/useCollectBillingDataMutation.graphql"
import { useConcludeDialogStoreContext } from "@/modules/patients/detail/store/useConcludeDialogStore"
import {
  getChProgressNotesByAppointmentId,
  getDeProgressNotesByAppointmentId,
} from "@/modules/patients/detail/store/useDocumentStore"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useContextAppointment } from "@/modules/patients/hooks/useContextAppointment"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { useConcludeAppointmentMutation } from "@/modules/patients/services/useConcludeAppointmentMutation"
import { useReConcludeAppointmentMutation } from "@/modules/patients/services/useReConcludeAppointmentMutation"
import { Country } from "@/types/__generated__/union-types"

// Resolves to whether the appointment was concluded here, so the caller knows to open the dialog.
type TryConcludeWithoutDialog = () => Promise<boolean>

const getProgressNoteIds = (country: Country, appointmentId: string): string[] => {
  const progressNotes =
    country === "CH"
      ? getChProgressNotesByAppointmentId(appointmentId)
      : getDeProgressNotesByAppointmentId(appointmentId)

  return progressNotes ? [...progressNotes.keys()] : []
}

const useConcludeWithoutDialog = (patientAge: number | undefined): readonly [TryConcludeWithoutDialog, boolean] => {
  const appointmentId = useAppointmentParam()
  const country = useCountryParam()
  const contextAppointment = useContextAppointment()
  const setSelectedDocument = useConcludeDialogStoreContext((state) => state.actions.setSelectedDocument)
  const setIsConcluded = useConcludeDialogStoreContext((state) => state.actions.setIsConcluded)
  const [concludeAppointment, isConcludingAppointment] = useConcludeAppointmentMutation()
  const [reConcludeAppointment, isReConcludingAppointment] = useReConcludeAppointmentMutation()

  const concludeWithoutDialog = async (concludedAppointmentId: string, concludedPatientAge: number) => {
    const collectBillingInput: CollectBillingDataInput = {
      appointmentId: concludedAppointmentId,
      isNotBillable: false,
      patientAge: concludedPatientAge,
    }

    if (contextAppointment?.isCompleted === false) {
      await concludeAppointment({
        id: concludedAppointmentId,
        shouldIncreaseMissedCall: false,
        collectBillingInput,
        agentFeedback: null,
      })
    } else {
      await reConcludeAppointment({
        id: concludedAppointmentId,
        collectBillingInput,
        progressNoteIds: getProgressNoteIds(country, concludedAppointmentId),
        isEditWithin24Hours: true,
      })
    }

    setSelectedDocument(null)
    setIsConcluded()
  }

  const tryConcludeWithoutDialog: TryConcludeWithoutDialog = async () => {
    // An administrative request produces no signable document and no billing choice, so the conclude
    // dialog would have nothing to ask the agent.
    const isAdministrativeRequest = contextAppointment?.service === "ADMINISTRATIVE_REQUEST"

    if (!appointmentId || !isAdministrativeRequest) {
      return false
    }

    if (patientAge === undefined) {
      throw new Error("Patient age is required to conclude an appointment.")
    }

    await concludeWithoutDialog(appointmentId, patientAge)

    return true
  }

  return [tryConcludeWithoutDialog, isConcludingAppointment || isReConcludingAppointment] as const
}

export { useConcludeWithoutDialog }
